'use client';
import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Calendar, Clock, Users, Phone, MessageSquare, CheckCircle, Loader2, Navigation, PhoneCall } from 'lucide-react';
import type { Vehicle } from './FleetSection';

interface BookingFormData {
  pickup: string;
  drop: string;
  date: string;
  time: string;
  passengers: string;
  phone: string;
  message: string;
}

type BookingStep = 'form' | 'searching' | 'driver_calling' | 'confirmed';

interface Props {
  vehicle: Vehicle;
  onClose: () => void;
}

const ADMIN_WHATSAPP = '918860044910';

export default function BookingModal({ vehicle, onClose }: Props) {
  const [step, setStep] = useState<BookingStep>('form');
  const [bookingData, setBookingData] = useState<BookingFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<BookingFormData>>({});
  const [searchDots, setSearchDots] = useState(0);
  const [callingRing, setCallingRing] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Searching animation dots
  useEffect(() => {
    if (step === 'searching') {
      const interval = setInterval(() => {
        setSearchDots((d) => (d + 1) % 4);
      }, 500);
      searchTimerRef.current = setTimeout(() => {
        clearInterval(interval);
        setStep('driver_calling');
      }, 3500);
      return () => {
        clearInterval(interval);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      };
    }
  }, [step]);

  // Calling ring animation
  useEffect(() => {
    if (step === 'driver_calling') {
      const interval = setInterval(() => setCallingRing((r) => !r), 800);
      callingTimerRef.current = setTimeout(() => {
        clearInterval(interval);
        setStep('confirmed');
      }, 4000);
      return () => {
        clearInterval(interval);
        if (callingTimerRef.current) clearTimeout(callingTimerRef.current);
      };
    }
  }, [step]);

  const validate = (data: BookingFormData): boolean => {
    const newErrors: Partial<BookingFormData> = {};
    if (!data.pickup.trim()) newErrors.pickup = 'Pickup location is required';
    if (!data.drop.trim()) newErrors.drop = 'Drop location is required';
    if (!data.date) newErrors.date = 'Date is required';
    if (!data.time) newErrors.time = 'Time is required';
    if (!data.passengers) newErrors.passengers = 'Select passenger count';
    if (!data.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(data.phone.trim())) {
      newErrors.phone = 'Enter valid 10-digit mobile number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildWhatsAppMessage = (data: BookingFormData): string => {
    const msg = [
      `🚖 *NEW CAB BOOKING - NAVYA TOUR & TRAVELS*`,
      ``,
      `🚗 *Vehicle:* ${vehicle.name} (${vehicle.type})`,
      `📍 *Pickup:* ${data.pickup}`,
      `📍 *Drop:* ${data.drop}`,
      `📅 *Date:* ${data.date}`,
      `⏰ *Time:* ${data.time}`,
      `👥 *Passengers:* ${data.passengers}`,
      `📞 *Customer Phone:* +91 ${data.phone}`,
      data.message ? `💬 *Note:* ${data.message}` : '',
      ``,
      `_Booking via Navya Tour & Travels website_`,
    ]
      .filter(Boolean)
      .join('\n');
    return encodeURIComponent(msg);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data: BookingFormData = {
      pickup: (form.elements.namedItem('pickup') as HTMLInputElement).value,
      drop: (form.elements.namedItem('drop') as HTMLInputElement).value,
      date: (form.elements.namedItem('date') as HTMLInputElement).value,
      time: (form.elements.namedItem('time') as HTMLInputElement).value,
      passengers: (form.elements.namedItem('passengers') as HTMLSelectElement).value,
      phone: (form.elements.namedItem('phone') as HTMLInputElement).value,
      message: (form.elements.namedItem('message') as HTMLTextAreaElement).value,
    };

    if (!validate(data)) return;

    setIsLoading(true);
    setBookingData(data);

    // Send to WhatsApp
    const waMsg = buildWhatsAppMessage(data);
    const waUrl = `https://wa.me/${ADMIN_WHATSAPP}?text=${waMsg}`;
    window.open(waUrl, '_blank');

    await new Promise((r) => setTimeout(r, 600));
    setIsLoading(false);
    setStep('searching');
  };

  return (
    <div>
      {/* Vehicle Header */}
      <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border/30">
        <img
          src={vehicle.image}
          alt={vehicle.imageAlt}
          className="w-16 h-12 object-cover rounded-lg"
        />
        <div>
          <h3 className="text-xl font-bold text-white">Book {vehicle.name}</h3>
          <p className="text-muted-foreground text-sm">
            {vehicle.seats} Seats • AC •{' '}
            <span className="text-green-400 font-semibold">Delhi Local Cab</span>
          </p>
        </div>
        <div className="ml-auto text-right hidden sm:block">
          <span className="text-xs text-muted-foreground block">Instant Booking</span>
          <span className="text-xs text-primary font-semibold">✅ No OTP Needed</span>
        </div>
      </div>

      {/* ── STEP: FORM ── */}
      {step === 'form' && (
        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Pickup */}
            <div>
              <label className="block text-sm font-semibold text-white mb-1.5">
                <MapPin size={14} className="inline mr-1 text-primary" />
                Pickup Location <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <Navigation size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
                <input
                  name="pickup"
                  placeholder="Enter pickup location"
                  className="input-dark w-full pl-9 pr-4 py-3 text-sm"
                />
              </div>
              {errors.pickup && <p className="text-red-400 text-xs mt-1">{errors.pickup}</p>}
            </div>

            {/* Drop */}
            <div>
              <label className="block text-sm font-semibold text-white mb-1.5">
                <MapPin size={14} className="inline mr-1 text-red-400" />
                Drop Location <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-red-400" />
                <input
                  name="drop"
                  placeholder="Where do you want to go?"
                  className="input-dark w-full pl-9 pr-4 py-3 text-sm"
                />
              </div>
              {errors.drop && <p className="text-red-400 text-xs mt-1">{errors.drop}</p>}
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-semibold text-white mb-1.5">
                <Calendar size={14} className="inline mr-1 text-primary" />
                Travel Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                name="date"
                className="input-dark w-full px-4 py-3 text-sm"
              />
              {errors.date && <p className="text-red-400 text-xs mt-1">{errors.date}</p>}
            </div>

            {/* Time */}
            <div>
              <label className="block text-sm font-semibold text-white mb-1.5">
                <Clock size={14} className="inline mr-1 text-primary" />
                Pickup Time <span className="text-red-400">*</span>
              </label>
              <input
                type="time"
                name="time"
                className="input-dark w-full px-4 py-3 text-sm"
              />
              {errors.time && <p className="text-red-400 text-xs mt-1">{errors.time}</p>}
            </div>

            {/* Passengers */}
            <div>
              <label className="block text-sm font-semibold text-white mb-1.5">
                <Users size={14} className="inline mr-1 text-primary" />
                Passengers <span className="text-red-400">*</span>
              </label>
              <select name="passengers" className="input-dark w-full px-4 py-3 text-sm">
                <option value="">Select count</option>
                {Array.from({ length: vehicle.seats }, (_, i) => i + 1).map((n) => (
                  <option key={`pax-${n}`} value={n}>
                    {n} Passenger{n > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
              {errors.passengers && <p className="text-red-400 text-xs mt-1">{errors.passengers}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-white mb-1.5">
                <Phone size={14} className="inline mr-1 text-primary" />
                Your Phone Number <span className="text-red-400">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                placeholder="Enter 10-digit mobile number"
                className="input-dark w-full px-4 py-3 text-sm"
                maxLength={10}
              />
              {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
            </div>
          </div>

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-white mb-1.5">
              <MessageSquare size={14} className="inline mr-1 text-primary" />
              Special Instructions (Optional)
            </label>
            <textarea
              name="message"
              rows={2}
              placeholder="Any special requirements or notes..."
              className="input-dark w-full px-4 py-3 text-sm resize-none"
            />
          </div>

          {/* Info box */}
          <div className="glass-card border border-primary/15 rounded-xl p-4 mb-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="text-primary font-semibold">📲 How it works:</span> After clicking{' '}
              <strong className="text-white">Confirm Booking</strong>, your details will be sent directly to{' '}
              <strong className="text-white">WhatsApp +91 88600 44910</strong>. The driver will contact you shortly.
            </p>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-4 rounded-xl text-base font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Sending Booking...
              </>
            ) : (
              '🚖 Confirm Booking'
            )}
          </button>
        </form>
      )}

      {/* ── STEP: SEARCHING (Uber/Rapido style ride searching animation) ── */}
      {step === 'searching' && (
        <div className="text-center py-10">
          {/* Animated radar rings */}
          <div className="relative w-32 h-32 mx-auto mb-8">
            <div className="absolute inset-0 rounded-full border-2 border-primary/10 animate-ping" style={{ animationDuration: '1.5s' }} />
            <div className="absolute inset-3 rounded-full border-2 border-primary/20 animate-ping" style={{ animationDuration: '1.5s', animationDelay: '0.3s' }} />
            <div className="absolute inset-6 rounded-full border-2 border-primary/30 animate-ping" style={{ animationDuration: '1.5s', animationDelay: '0.6s' }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-3xl animate-pulse">
                🚖
              </div>
            </div>
          </div>

          <h3 className="text-xl font-bold text-white mb-2">
            Searching for Driver{'.'.repeat(searchDots)}
          </h3>
          <p className="text-muted-foreground text-sm mb-1">
            Finding the nearest available driver for you
          </p>
          <p className="text-xs text-primary font-semibold mb-6">
            📍 {bookingData?.pickup} → {bookingData?.drop}
          </p>

          {/* Animated route line */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
            <div className="flex gap-1">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={`line-${i}`}
                  className="w-4 h-0.5 bg-primary/40 rounded-full animate-pulse"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
            <div className="w-3 h-3 rounded-full bg-red-400 animate-pulse" />
          </div>

          <div className="flex justify-center gap-1.5">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={`dot-${i}`}
                className="w-2 h-2 rounded-full bg-primary animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── STEP: DRIVER CALLING NOTIFICATION ── */}
      {step === 'driver_calling' && (
        <div className="text-center py-8">
          {/* Calling ring animation */}
          <div className="relative w-28 h-28 mx-auto mb-6">
            <div
              className={`absolute inset-0 rounded-full border-4 border-green-400/30 transition-all duration-700 ${
                callingRing ? 'scale-110 opacity-100' : 'scale-100 opacity-50'
              }`}
            />
            <div
              className={`absolute inset-2 rounded-full border-4 border-green-400/50 transition-all duration-700 ${
                callingRing ? 'scale-105 opacity-100' : 'scale-100 opacity-60'
              }`}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-green-500/15 border-2 border-green-500/40 flex items-center justify-center">
                <PhoneCall
                  size={32}
                  className={`text-green-400 transition-transform duration-300 ${callingRing ? 'rotate-12' : '-rotate-6'}`}
                />
              </div>
            </div>
          </div>

          <h3 className="text-xl font-bold text-white mb-1">Driver is Calling You!</h3>
          <p className="text-green-400 font-semibold text-sm mb-1">📞 +91 88600 44910</p>
          <p className="text-muted-foreground text-xs mb-6">
            Please pick up — the driver will confirm your booking details
          </p>

          {/* Booking summary card */}
          <div className="glass-card border border-green-500/20 rounded-xl p-4 text-left mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-lg">👨‍✈️</div>
              <div>
                <p className="font-semibold text-white text-sm">Navya Travels Driver</p>
                <p className="text-xs text-muted-foreground">Verified Driver • ⭐ 4.8</p>
              </div>
              <span className="ml-auto text-xs text-green-400 font-semibold animate-pulse">Calling...</span>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                🚗 <span className="text-white font-medium">{vehicle.name}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                📍 Pickup: <span className="text-white">{bookingData?.pickup}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                📍 Drop: <span className="text-white">{bookingData?.drop}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                📅 {bookingData?.date} at {bookingData?.time}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP: CONFIRMED ── */}
      {step === 'confirmed' && (
        <div className="text-center py-8">
          <div className="w-20 h-20 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={36} className="text-green-400" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Booking Confirmed! 🎉</h3>
          <p className="text-lg text-green-400 font-semibold mb-1">
            Driver will contact you shortly.
          </p>
          <p className="text-muted-foreground text-sm mb-6">
            Your booking details have been sent to the driver at{' '}
            <span className="text-primary font-semibold">+91 88600 44910</span>
          </p>

          <div className="glass-card border border-green-500/20 rounded-xl p-4 text-left mb-6">
            <p className="text-xs font-semibold text-white mb-2">📋 Booking Summary</p>
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                🚗 Vehicle: <span className="text-white">{vehicle.name}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                📍 Pickup: <span className="text-white">{bookingData?.pickup}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                📍 Drop: <span className="text-white">{bookingData?.drop}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                📅 Date & Time: <span className="text-white">{bookingData?.date} at {bookingData?.time}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                👥 Passengers: <span className="text-white">{bookingData?.passengers}</span>
              </p>
              <p className="text-xs text-amber-300 mt-2">
                💬 Fare will be shared by driver after enquiry.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <a
              href="tel:+918860044910"
              className="flex-1 btn-ghost py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-1"
            >
              📞 Call Driver
            </a>
            <a
              href={`https://wa.me/${ADMIN_WHATSAPP}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-green-600 hover:bg-green-500 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-1 transition-colors"
            >
              💬 WhatsApp
            </a>
          </div>
          <button
            onClick={onClose}
            className="btn-primary w-full py-3 rounded-xl text-sm font-bold mt-3"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}